import React, { useState } from 'react';
import { getLegacyFormats } from '../utils/timeUtils';
import { Database, Binary, FileDigit, Smartphone } from 'lucide-react';

const Specialized: React.FC = () => {
  const [input, setInput] = useState<string>(Math.floor(Date.now() / 1000).toString());
  
  const ts = parseInt(input) || 0;
  const formats = getLegacyFormats(ts);

  const cards = [
    { name: 'Hexadecimal', value: `0x${ts.toString(16).toUpperCase()}`, icon: <Binary className="text-purple-500"/>, desc: 'Base-16 representation' },
    { name: 'Octal', value: `0${ts.toString(8)}`, icon: <Binary className="text-purple-500"/>, desc: 'Base-8 representation' },
    { name: 'Binary', value: ts.toString(2), icon: <Binary className="text-purple-500"/>, desc: 'Base-2 representation' },
    { name: 'LDAP / Active Directory', value: formats.ldap.toLocaleString('fullwide', { useGrouping: false }), icon: <Database className="text-blue-500"/>, desc: '100-nanosecond intervals since Jan 1, 1601' },
    { name: 'WebKit / Chrome', value: formats.webkit.toLocaleString('fullwide', { useGrouping: false }), icon: <GlobeIcon className="text-green-500"/>, desc: 'Microseconds since Jan 1, 1601' },
    { name: 'Cocoa / Core Data', value: formats.cocoa.toFixed(1), icon: <Smartphone className="text-slate-500"/>, desc: 'Seconds since Jan 1, 2001 (Mac/iOS)' },
    { name: 'Mac HFS+', value: formats.hfs.toString(), icon: <FileDigit className="text-red-500"/>, desc: 'Seconds since Jan 1, 1904' },
    { name: 'Excel (Windows)', value: formats.excel.toFixed(5), icon: <FileDigit className="text-green-600"/>, desc: 'Days since Dec 30, 1899' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h2 className="text-xl font-bold text-slate-800 mb-4">Specialized Converter</h2>
        <p className="text-slate-500 mb-4">Enter a Unix timestamp to convert it into various system-specific and legacy formats.</p>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <input 
            type="number" 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none font-mono text-lg text-slate-800"
            placeholder="Epoch Timestamp"
          />
          <button 
            onClick={() => setInput(Math.floor(Date.now() / 1000).toString())}
            className="px-6 py-3 bg-sky-50 text-sky-700 font-medium rounded-lg hover:bg-sky-100 transition-colors whitespace-nowrap"
          >
            Use Current
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, i) => (
          <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 font-semibold text-slate-700">
                {card.icon}
                {card.name}
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg mb-2">
              <code className="text-sm font-mono text-slate-800 break-all">{card.value}</code>
            </div>
            <p className="text-xs text-slate-400">{card.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Simple Icon Wrappers to avoid missing icon errors if not exported from lucide
const GlobeIcon = ({className}:{className?:string}) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
);

export default Specialized;